import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  ADMIN_SEARCH_QUERY_MAX_LENGTH,
  ADMIN_STREAMS,
  BOARD_VISIBILITIES,
  SUPERADMIN_AUDIT_ACTIONS,
  adminApiTokenIssuedSchema,
  adminApiTokenListSchema,
  adminApiTokenSchema,
  adminAuditPageSchema,
  adminBoardPageSchema,
  adminCreatedUserSchema,
  adminSessionsRevokeResultSchema,
  adminUserDetailWithInitialsSchema,
  adminUserPageSchema,
  adminWorkspacePageSchema,
  ulidSchema,
  type AdminApiToken,
  type AdminApiTokenIssued,
  type AdminApiTokenIssueBody,
  type AdminApiTokenList,
  type AdminAuditPage,
  type AdminBoardPage,
  type AdminCreatedUser,
  type AdminSessionsRevokeResult,
  type AdminStream,
  type AdminUserCreateBody,
  type AdminUserDetailWithInitials,
  type AdminUserInitialsBody,
  type AdminUserLifecycleBody,
  type AdminUserPage,
  type AdminWorkspacePage,
  type ApiError,
  type BoardVisibility,
  type SuperadminAuditAction,
} from '@molt/shared';
import { del, emptyResponseSchema, get, patch, post } from './api';
import { queryKeys } from './query-keys';
import { isSuperadminSession, useSession } from './session';

/**
 * SA-9 — the platform admin console's data layer, over SA-8's `GET|POST
 * /admin/*` (`apps/server/src/routes/admin.ts`, contracts in
 * `packages/shared/src/admin.ts`).
 *
 * ---------------------------------------------------------------------------
 * ⚠ EVERY READ IS GATED ON `isSuperadminSession`, INSIDE THE HOOK
 * ---------------------------------------------------------------------------
 * The server answers a non-superadmin with the ordinary unmatched-route 404, so
 * an ungated read leaks nothing — but it is still a request the brief says must
 * not happen ("no admin request returns data"), and a 404 from a query is
 * indistinguishable in the console from a real outage. The gate lives here and
 * not at the call site so no future caller can forget it. `routes/admin.test.tsx`
 * counts `/api/admin` fetches for a normal account and expects zero.
 *
 * ---------------------------------------------------------------------------
 * "LOAD MORE", NOT `useInfiniteQuery`
 * ---------------------------------------------------------------------------
 * Nothing in `apps/web` uses `useInfiniteQuery`, and a second paging idiom for
 * one page is not worth its cost. Only the FIRST page lives under a list key;
 * `useAdminLoadMore` fetches the next page with `fetchQuery` and appends it into
 * that same entry with `setQueryData`. A refetch of the entry (an invalidation
 * after an account action) therefore collapses back to page one, which is the
 * honest answer after a write: the rows already shown may have changed.
 *
 * ⚠ So the list queries opt out of the three refetches nobody asked for
 * (`ADMIN_LIST_REFETCH`). `main.tsx` refetches a stale query on window focus,
 * on reconnect and on mount, and each one fetched page one alone and replaced
 * the merged entry: a superadmin who pressed "Load more", switched to their
 * ticket and came back found the appended rows gone, with nothing to say why.
 * The mount is the common one — `/admin/users/$userId` renders through
 * `<Outlet/>`, which unmounts the console, so Back from an account opened off
 * page two mounts the list again. Focus, reconnect and a remount say nothing
 * about the rows having changed; a write does, and it still invalidates.
 *
 * ⚠ The mount opt-out is a FUNCTION, not `false`. An account action runs on
 * the account page, while the list is unmounted, so its invalidation only
 * marks the entry; the mount on Back is the one moment it can be honoured, and
 * `refetchOnMount: false` would skip that too and keep calling a deactivated
 * account active. `routes/admin.test.tsx` holds both halves.
 *
 * The console is a support tool read on demand, not a live board. A fresh read
 * is one click away: a changed filter or a tab click (a disabled query being
 * enabled refetches a stale entry through a path `refetchOnMount` does not
 * govern, and a tab click is asking for the list again).
 *
 * ⚠ `fetchQuery` has no `enabled`. It is called from `loadMore` alone, which
 * only a click reaches, and it refuses to run without a cached first page —
 * which only a gated query can have written.
 */

// ---------------------------------------------------------------------------
// The console's address
// ---------------------------------------------------------------------------

/**
 * `/admin`'s query string: which tab, and that tab's filters. Everything is
 * optional; an absent `tab` is the Users tab, so `/admin` is the brief's
 * landing ("the Users tab loads").
 */
export interface AdminConsoleSearch {
  readonly tab?: AdminStream;
  readonly q?: string;
  readonly visibility?: BoardVisibility;
  readonly closed?: boolean;
  readonly action?: SuperadminAuditAction;
  readonly actor?: string;
  readonly target?: string;
}

function includes<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value);
}

/**
 * `validateSearch` for `/admin`. A value the server would 422 is dropped here
 * rather than sent — a hand-edited `?closed=yes` renders the unfiltered list,
 * not an error page — and the default tab is omitted so the URL stays `/admin`.
 */
export function adminConsoleSearchFrom(raw: Record<string, unknown>): AdminConsoleSearch {
  const tab =
    includes(ADMIN_STREAMS, raw.tab) && raw.tab !== 'users' ? raw.tab : undefined;
  const q =
    typeof raw.q === 'string' &&
    raw.q.trim() !== '' &&
    raw.q.trim().length <= ADMIN_SEARCH_QUERY_MAX_LENGTH
      ? raw.q.trim()
      : undefined;
  const visibility = includes(BOARD_VISIBILITIES, raw.visibility)
    ? raw.visibility
    : undefined;
  const closed =
    raw.closed === true || raw.closed === 'true'
      ? true
      : raw.closed === false || raw.closed === 'false'
        ? false
        : undefined;
  const action = includes(SUPERADMIN_AUDIT_ACTIONS, raw.action) ? raw.action : undefined;
  const actor = ulidSchema.safeParse(raw.actor).success
    ? (raw.actor as string)
    : undefined;
  const target = ulidSchema.safeParse(raw.target).success
    ? (raw.target as string)
    : undefined;
  return {
    ...(tab === undefined ? {} : { tab }),
    ...(q === undefined ? {} : { q }),
    ...(visibility === undefined ? {} : { visibility }),
    ...(closed === undefined ? {} : { closed }),
    ...(action === undefined ? {} : { action }),
    ...(actor === undefined ? {} : { actor }),
    ...(target === undefined ? {} : { target }),
  };
}

/** The tab a search names; `users` when it names none. */
export function adminConsoleTab(search: AdminConsoleSearch): AdminStream {
  return search.tab ?? 'users';
}

// ---------------------------------------------------------------------------
// Filters, querystrings and keys
// ---------------------------------------------------------------------------

export interface AdminUserFilters {
  readonly q?: string | undefined;
}
export interface AdminWorkspaceFilters {
  readonly q?: string | undefined;
}
export interface AdminBoardFilters {
  readonly q?: string | undefined;
  readonly visibility?: BoardVisibility | undefined;
  readonly closed?: boolean | undefined;
}
export interface AdminAuditFilters {
  readonly actor?: string | undefined;
  readonly target?: string | undefined;
  readonly action?: SuperadminAuditAction | undefined;
}

/**
 * A stable querystring for a filter set, in a FIXED key order, so one filter
 * set is one cache entry however its object was spelled. The cursor is never
 * part of it — see "LOAD MORE" above.
 */
export function adminFilterString(
  filters: Readonly<Record<string, string | boolean | undefined>>,
): string {
  const params = new URLSearchParams();
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (value !== undefined) params.set(key, String(value));
  }
  return params.toString();
}

function withCursor(filterString: string, cursor: string | undefined): string {
  const params = new URLSearchParams(filterString);
  if (cursor !== undefined) params.set('cursor', cursor);
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** `GET /admin/users` */
export function listAdminUsers(
  filters: AdminUserFilters,
  cursor?: string,
): Promise<AdminUserPage> {
  return get(
    `/admin/users${withCursor(adminFilterString({ q: filters.q }), cursor)}`,
    adminUserPageSchema,
  );
}

/** `GET /admin/users/:id` */
export function getAdminUser(userId: string): Promise<AdminUserDetailWithInitials> {
  return get(
    `/admin/users/${encodeURIComponent(userId)}`,
    adminUserDetailWithInitialsSchema,
  );
}

/**
 * `POST /admin/users` — `201` with the new account and, when the server chose
 * it, the password, once (`docs/notes/admin-user-creation.md` UC-D3).
 */
export function createAdminUser(body: AdminUserCreateBody): Promise<AdminCreatedUser> {
  return post('/admin/users', adminCreatedUserSchema, body);
}

/** `GET /admin/workspaces` */
export function listAdminWorkspaces(
  filters: AdminWorkspaceFilters,
  cursor?: string,
): Promise<AdminWorkspacePage> {
  return get(
    `/admin/workspaces${withCursor(adminFilterString({ q: filters.q }), cursor)}`,
    adminWorkspacePageSchema,
  );
}

/** `GET /admin/boards` */
export function listAdminBoards(
  filters: AdminBoardFilters,
  cursor?: string,
): Promise<AdminBoardPage> {
  return get(
    `/admin/boards${withCursor(boardFilterString(filters), cursor)}`,
    adminBoardPageSchema,
  );
}

/** `GET /admin/audit` */
export function listAdminAudit(
  filters: AdminAuditFilters,
  cursor?: string,
): Promise<AdminAuditPage> {
  return get(
    `/admin/audit${withCursor(auditFilterString(filters), cursor)}`,
    adminAuditPageSchema,
  );
}

/**
 * `POST /admin/users/:id/deactivate` — `204`, so `emptyResponseSchema`: `request`
 * reads an empty body as `null`, and any other schema would report a success as
 * "the server sent an unexpected shape".
 */
export function deactivateAdminUser(
  userId: string,
  body: AdminUserLifecycleBody,
): Promise<null> {
  return post(
    `/admin/users/${encodeURIComponent(userId)}/deactivate`,
    emptyResponseSchema,
    body,
  );
}

/** `POST /admin/users/:id/reactivate` — `204`, as above. */
export function reactivateAdminUser(
  userId: string,
  body: AdminUserLifecycleBody,
): Promise<null> {
  return post(
    `/admin/users/${encodeURIComponent(userId)}/reactivate`,
    emptyResponseSchema,
    body,
  );
}

/** `POST /admin/users/:id/sessions/revoke` — `200` with the count, zero included. */
export function revokeAdminUserSessions(
  userId: string,
  body: AdminUserLifecycleBody,
): Promise<AdminSessionsRevokeResult> {
  return post(
    `/admin/users/${encodeURIComponent(userId)}/sessions/revoke`,
    adminSessionsRevokeResultSchema,
    body,
  );
}

/**
 * `PATCH /admin/users/:id/initials` — member initials (owner-directed;
 * `docs/notes/member-initials.md`). `200` with the same widened detail
 * `GET /admin/users/:id` answers, so the page can show the stored override at
 * once rather than after a refetch.
 */
export function setAdminUserInitials(
  userId: string,
  body: AdminUserInitialsBody,
): Promise<AdminUserDetailWithInitials> {
  return patch(
    `/admin/users/${encodeURIComponent(userId)}/initials`,
    adminUserDetailWithInitialsSchema,
    body,
  );
}

function boardFilterString(filters: AdminBoardFilters): string {
  return adminFilterString({
    q: filters.q,
    visibility: filters.visibility,
    closed: filters.closed,
  });
}

function auditFilterString(filters: AdminAuditFilters): string {
  return adminFilterString({
    actor: filters.actor,
    target: filters.target,
    action: filters.action,
  });
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The list queries' refetch policy — see "LOAD MORE" above. Not on
 * `useAdminUser`: the account page holds one object, no appended page, and a
 * focus refetch there is the ordinary freshness every other page has.
 */
/**
 * Typed on the one field it reads, so it fits every list's `Query<…>` without
 * a generic the `as const` object below could not carry.
 */
function refetchWhenInvalidated(query: {
  readonly state: { readonly isInvalidated: boolean };
}): boolean {
  return query.state.isInvalidated;
}

const ADMIN_LIST_REFETCH = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: refetchWhenInvalidated,
} as const;

/** The gate, once. `extra` narrows it further (a tab that is not showing). */
function useAdminEnabled(extra: boolean): boolean {
  return isSuperadminSession(useSession()) && extra;
}

export function useAdminUsers(
  filters: AdminUserFilters,
  enabled = true,
): UseQueryResult<AdminUserPage, ApiError> {
  return useQuery({
    queryKey: queryKeys.admin.users(adminFilterString({ q: filters.q })),
    queryFn: () => listAdminUsers(filters),
    enabled: useAdminEnabled(enabled),
    ...ADMIN_LIST_REFETCH,
  });
}

export function useAdminUser(
  userId: string,
): UseQueryResult<AdminUserDetailWithInitials, ApiError> {
  return useQuery({
    queryKey: queryKeys.admin.user(userId),
    queryFn: () => getAdminUser(userId),
    enabled: useAdminEnabled(true),
  });
}

export function useAdminWorkspaces(
  filters: AdminWorkspaceFilters,
  enabled = true,
): UseQueryResult<AdminWorkspacePage, ApiError> {
  return useQuery({
    queryKey: queryKeys.admin.workspaces(adminFilterString({ q: filters.q })),
    queryFn: () => listAdminWorkspaces(filters),
    enabled: useAdminEnabled(enabled),
    ...ADMIN_LIST_REFETCH,
  });
}

export function useAdminBoards(
  filters: AdminBoardFilters,
  enabled = true,
): UseQueryResult<AdminBoardPage, ApiError> {
  return useQuery({
    queryKey: queryKeys.admin.boards(boardFilterString(filters)),
    queryFn: () => listAdminBoards(filters),
    enabled: useAdminEnabled(enabled),
    ...ADMIN_LIST_REFETCH,
  });
}

export function useAdminAudit(
  filters: AdminAuditFilters,
  enabled = true,
): UseQueryResult<AdminAuditPage, ApiError> {
  return useQuery({
    queryKey: queryKeys.admin.audit(auditFilterString(filters)),
    queryFn: () => listAdminAudit(filters),
    enabled: useAdminEnabled(enabled),
    ...ADMIN_LIST_REFETCH,
  });
}

// ---------------------------------------------------------------------------
// Load more
// ---------------------------------------------------------------------------

interface AdminPage<Item extends { readonly id: string }> {
  readonly items: readonly Item[];
  readonly nextCursor: string | null;
}

/**
 * The next page appended to the current one. Rows are de-duplicated by id: the
 * keyset cannot overlap on its own, but a row created between two pages of a
 * list that was refetched in the meantime could, and a duplicate id is a
 * duplicate React key.
 */
export function mergeAdminPages<
  Item extends { readonly id: string },
  Page extends AdminPage<Item>,
>(current: Page | undefined, next: Page): Page {
  if (current === undefined) return next;
  const seen = new Set(current.items.map((item) => item.id));
  return {
    ...next,
    items: [...current.items, ...next.items.filter((item) => !seen.has(item.id))],
    nextCursor: next.nextCursor,
  };
}

export interface AdminLoadMore {
  readonly loadMore: () => Promise<void>;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
}

/**
 * "Load more" for one list entry. `queryKey` is the entry's own key, and
 * `fetchPage` fetches the page after `cursor` with the SAME filters that built
 * that key.
 */
export function useAdminLoadMore<
  Item extends { readonly id: string },
  Page extends AdminPage<Item>,
>(queryKey: QueryKey, fetchPage: (cursor: string) => Promise<Page>): AdminLoadMore {
  const client = useQueryClient();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const loadMore = useCallback(async () => {
    const current = client.getQueryData<Page>(queryKey);
    const cursor = current?.nextCursor;
    if (cursor === null || cursor === undefined) return;
    setLoading(true);
    setError(null);
    try {
      const next = await client.fetchQuery({
        queryKey: [...queryKey, 'after', cursor],
        queryFn: () => fetchPage(cursor),
        gcTime: 0,
      });
      client.setQueryData<Page>(queryKey, (previous) => mergeAdminPages(previous, next));
    } catch (cause) {
      setError(cause as ApiError);
    } finally {
      setLoading(false);
    }
  }, [client, fetchPage, queryKey]);

  return { loadMore, isLoading, error };
}

/** The list-specific wrappers, so a caller never rebuilds a key by hand. */
export function useAdminUsersLoadMore(filters: AdminUserFilters): AdminLoadMore {
  const q = filters.q;
  return useAdminLoadMore(
    queryKeys.admin.users(adminFilterString({ q })),
    useCallback((cursor: string) => listAdminUsers({ q }, cursor), [q]),
  );
}

export function useAdminWorkspacesLoadMore(
  filters: AdminWorkspaceFilters,
): AdminLoadMore {
  const q = filters.q;
  return useAdminLoadMore(
    queryKeys.admin.workspaces(adminFilterString({ q })),
    useCallback((cursor: string) => listAdminWorkspaces({ q }, cursor), [q]),
  );
}

export function useAdminBoardsLoadMore(filters: AdminBoardFilters): AdminLoadMore {
  const { q, visibility, closed } = filters;
  return useAdminLoadMore(
    queryKeys.admin.boards(boardFilterString({ q, visibility, closed })),
    useCallback(
      (cursor: string) => listAdminBoards({ q, visibility, closed }, cursor),
      [q, visibility, closed],
    ),
  );
}

export function useAdminAuditLoadMore(filters: AdminAuditFilters): AdminLoadMore {
  const { actor, target, action } = filters;
  return useAdminLoadMore(
    queryKeys.admin.audit(auditFilterString({ actor, target, action })),
    useCallback(
      (cursor: string) => listAdminAudit({ actor, target, action }, cursor),
      [actor, target, action],
    ),
  );
}

// ---------------------------------------------------------------------------
// Account actions
// ---------------------------------------------------------------------------

/**
 * One mutation per act. On success every `['admin', 'users', …]` entry — the
 * detail and every filtered list — and the audit log are invalidated; nothing
 * is written optimistically, because each of the three can be refused with a
 * 409 the client cannot predict (D11's last-admin rule).
 */
function useAdminUserAction<Result>(
  userId: string,
  act: (userId: string, body: AdminUserLifecycleBody) => Promise<Result>,
): UseMutationResult<Result, ApiError, AdminUserLifecycleBody> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => act(userId, body),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.admin.users() }),
        client.invalidateQueries({ queryKey: queryKeys.admin.audit() }),
      ]);
    },
  });
}

export function useDeactivateAdminUser(
  userId: string,
): UseMutationResult<null, ApiError, AdminUserLifecycleBody> {
  return useAdminUserAction(userId, deactivateAdminUser);
}

export function useReactivateAdminUser(
  userId: string,
): UseMutationResult<null, ApiError, AdminUserLifecycleBody> {
  return useAdminUserAction(userId, reactivateAdminUser);
}

export function useRevokeAdminUserSessions(
  userId: string,
): UseMutationResult<AdminSessionsRevokeResult, ApiError, AdminUserLifecycleBody> {
  return useAdminUserAction(userId, revokeAdminUserSessions);
}

/**
 * Member initials — set (`"AB"`) or clear (`null`) the initials an account's
 * avatar circle shows everywhere.
 *
 * A `useMutation`, unlike the token and password flows below: the response is
 * the account detail and carries no secret, so it may sit in the
 * `MutationCache`.
 *
 * On success the 200 is written into `admin.user(id)` (it is exactly what that
 * entry reads), and then every surface that can draw this account's avatar is
 * marked stale: the admin lists and the audit log (one `super.user_initials_set`
 * row), and the member-facing prefixes whose responses embed a public profile —
 * `boards` (contents, members), `lists`, `cards` (the card back, comments),
 * `workspaces`, `workspace-views` (the table view's members, a view's
 * creator), `enterprises` (the managed-member list), `users`, `notifications`
 * and `me` (the session's own profile, when a superadmin sets their own
 * initials, and the home and inbox surfaces).
 * The server emits no event for this act (an account has no `board_id`), so
 * this sweep is the only refresh there is — and only in THIS browser. Another
 * member's open board keeps the old initials until it next reads its members.
 * Inactive entries are only marked; each refetches when its page next mounts.
 */
export function useSetAdminUserInitials(
  userId: string,
): UseMutationResult<AdminUserDetailWithInitials, ApiError, AdminUserInitialsBody> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => setAdminUserInitials(userId, body),
    onSuccess: async (detail) => {
      // `detail.id`, not the closure's `userId`: the route outlives a `$userId`
      // change, and TanStack Query hands a PENDING mutation the observer's new
      // options — so a save still in flight when the superadmin moves to
      // another account would write this response into that account's entry.
      client.setQueryData(queryKeys.admin.user(detail.id), detail);
      await Promise.all(
        [
          queryKeys.admin.users(),
          queryKeys.admin.audit(),
          queryKeys.boards.all(),
          queryKeys.lists.all(),
          queryKeys.cards.all(),
          queryKeys.workspaces.all(),
          queryKeys.workspaceViews.all(),
          queryKeys.enterprises.all(),
          queryKeys.users.all(),
          queryKeys.notifications.all(),
          queryKeys.me(),
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
    },
  });
}

/**
 * UC-4 — create an account and hand the 201 to the CALLER only.
 *
 * ⚠ **Not `useMutation`**, for `useIssueAdminApiToken`'s reason below: the
 * response may carry `generatedPassword`, and a mutation's result sits in the
 * `MutationCache` for `gcTime` after its observer is gone. Closing the
 * hand-over panel must be the only forgetting there is to do.
 *
 * Every `['admin', 'users', …]` entry is marked stale — the new account belongs
 * in whichever filtered list is read next — and so is every audit list, which
 * gains a `super.user_created` row. Nothing is written into the cache.
 */
export function useCreateAdminUser(): (
  body: AdminUserCreateBody,
) => Promise<AdminCreatedUser> {
  const client = useQueryClient();
  return useCallback(
    async (body: AdminUserCreateBody) => {
      const created = await createAdminUser(body);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.admin.users() }),
        client.invalidateQueries({ queryKey: queryKeys.admin.audit() }),
      ]);
      return created;
    },
    [client],
  );
}

// ---------------------------------------------------------------------------
// API tokens (B85, AT-12)
// ---------------------------------------------------------------------------

/**
 * AT-5 and AT-3 — one account's API tokens, and the two superadmin acts on them
 * (`apps/server/src/routes/admin-api-tokens.ts`).
 *
 * ⚠ ONE RESPONSE HERE CARRIES A TOKEN: issuance's 201, parsed with
 * `adminApiTokenIssuedSchema`, so the superadmin can hand it over once
 * (`docs/notes/admin-user-creation.md` UC-D8, reversing B85 D4). The list and
 * the revoke stay on `adminApiTokenSchema`, which has no token field (only the
 * four-character `tokenHint`). Nothing below is written to the cache — the
 * list is re-read after each act — and the issue is not a `useMutation`
 * either (`useIssueAdminApiToken`), so the token reaches no cache at all.
 *
 * The list is gated like every other admin read (`useAdminEnabled`), and its key
 * sits under `admin.users()` so an account action on the same page refreshes it.
 */

/** `GET /admin/users/:userId/api-tokens` */
export function listAdminApiTokens(userId: string): Promise<AdminApiTokenList> {
  return get(
    `/admin/users/${encodeURIComponent(userId)}/api-tokens`,
    adminApiTokenListSchema,
  );
}

/** `POST /admin/users/:userId/api-tokens` — `201` with the active grant and its token, once. */
export function issueAdminApiToken(
  userId: string,
  body: AdminApiTokenIssueBody,
): Promise<AdminApiTokenIssued> {
  return post(
    `/admin/users/${encodeURIComponent(userId)}/api-tokens`,
    adminApiTokenIssuedSchema,
    body,
  );
}

/** `DELETE /admin/api-tokens/:id` — `200` with the revoked grant; a repeat is still `200`. */
export function revokeAdminApiToken(connectedAppId: string): Promise<AdminApiToken> {
  return del(
    `/admin/api-tokens/${encodeURIComponent(connectedAppId)}`,
    adminApiTokenSchema,
  );
}

export function useAdminApiTokens(
  userId: string,
): UseQueryResult<AdminApiTokenList, ApiError> {
  return useQuery({
    queryKey: queryKeys.admin.apiTokens(userId),
    queryFn: () => listAdminApiTokens(userId),
    enabled: useAdminEnabled(true),
  });
}

/**
 * Both acts refresh the account's token list and every audit list: each writes
 * a `super.api_token_*` audit row the Audit tab must not miss.
 */
function invalidateAdminApiTokenAct(client: QueryClient, userId: string): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.admin.apiTokens(userId) }),
    client.invalidateQueries({ queryKey: queryKeys.admin.audit() }),
  ]).then(() => undefined);
}

/**
 * UC-D8 / UC-D9 — issue a token and hand it to the CALLER only.
 *
 * ⚠ **Deliberately not `useMutation`**, for the reason
 * `useRevealConnectedAppToken` gives (AT-13): TanStack keeps a mutation's
 * result in `mutation.state.data` inside the `MutationCache` for as long as an
 * observer holds it and for `gcTime` after, where devtools and
 * `getMutationCache().getAll()` read it. So the token travels from the response
 * to the promise the caller awaits, and closing the caller's panel is the only
 * forgetting there is to do. Busy and error state belong to the caller.
 */
export function useIssueAdminApiToken(
  userId: string,
): (body: AdminApiTokenIssueBody) => Promise<AdminApiTokenIssued> {
  const client = useQueryClient();
  return useCallback(
    async (body: AdminApiTokenIssueBody) => {
      const issued = await issueAdminApiToken(userId, body);
      await invalidateAdminApiTokenAct(client, userId);
      return issued;
    },
    [client, userId],
  );
}

/** The revoke's row carries no token, so it may sit in the `MutationCache`. */
export function useRevokeAdminApiToken(
  userId: string,
): UseMutationResult<AdminApiToken, ApiError, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: revokeAdminApiToken,
    onSuccess: () => invalidateAdminApiTokenAct(client, userId),
  });
}
